import { Row } from "@components/Flex";
import { NoDataDisclaimer } from "@components/Goals/NoDataDisclaimer";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { NODE_COUNT } from "@constants";
import { useGoalDetailsBreakdown, useGoalsContext } from "@hooks";
import { i18n, Translations } from "@i18n";
import { convertCentsToDollar, formatCurrency, formatSign } from "@utils";
import { useStyles } from "./styles";
import { GoalInvestmentPerformanceProps } from "./types";

export const GoalInvestmentPerformance = ({ style }: GoalInvestmentPerformanceProps) => {
  const styles = useStyles();

  const { beneficiaryId, goalId } = useGoalsContext();

  const { data, loading, error } = useGoalDetailsBreakdown({
    variables: {
      beneficiaryId,
      goalId: goalId!,
      nodeCount: NODE_COUNT,
    },
    fetchPolicy: "cache-first",
  });

  if (!data || loading) return null;

  const { contributionsInCents, returnsInCents, totalValueInCents } = data.portfolioBreakdown;

  const hasNoData =
    error || contributionsInCents == null || returnsInCents == null || totalValueInCents == null;

  if (hasNoData) {
    return (
      <>
        <NoDataDisclaimer />
        <Spacing size={SpacingSizes.S} />
      </>
    );
  }

  const netInvestments = formatCurrency(convertCentsToDollar(contributionsInCents), true);

  const returns = `${formatSign(returnsInCents)}${formatCurrency(
    convertCentsToDollar(Math.abs(returnsInCents)),
    true,
  )}`;

  const total = formatCurrency(convertCentsToDollar(totalValueInCents), true);

  return (
    <div style={{ ...styles.container, ...style }}>
      <Body style={styles.title}>{i18n.t(Translations.GoalInvestmentPerformanceTitle)}</Body>
      <Spacing size={SpacingSizes.M} />

      <Row>
        <Body style={styles.left}>
          {i18n.t(Translations.GoalInvestmentPerformanceNetInvestments)}
        </Body>
        <Body>{netInvestments}</Body>
      </Row>
      <Spacing size={SpacingSizes.M} />

      <Row>
        <Body style={styles.left}>{i18n.t(Translations.GoalInvestmentPerformanceReturns)}</Body>
        <Body>{returns}</Body>
      </Row>
      <Spacing size={SpacingSizes.M} />

      <Row>
        <Body style={styles.left}>{i18n.t(Translations.GoalInvestmentPerformanceTotal)}</Body>
        <Body>{total}</Body>
      </Row>
    </div>
  );
};
